import type { OutletSummary, TaxRounding, TaxRuleInput, TaxRuleView } from "@kopi/contracts";
import { PRODUCT_KINDS, type ProductKind } from "@kopi/contracts/codes";
import { priceTax } from "@kopi/domain";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { Checkbox } from "@kopi/ui/components/checkbox";
import { ConfirmButton } from "@kopi/ui/components/confirm-button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { DatePicker } from "@kopi/ui/components/date-picker";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Switch } from "@kopi/ui/components/switch";
import { useInfiniteQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PencilIcon, PlusIcon, ReceiptTextIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { percentText } from "@/discounts/labels";
import { tax } from "@/i18n/messages/tax";
import { type AreaTranslate, useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { ApiRequestError, api, unwrap } from "@/lib/api";
import { basisPointsInput, parseBasisPoints } from "@/lib/percent";
import { meScopesQuery, outletsQuery } from "@/lib/queries";
import { taxRulesKey, taxRulesQuery } from "@/tax/queries";
import { ruleInForce, scheduledRules, type TaxVersionStatus, versionStatus } from "@/tax/rules";

export const Route = createFileRoute("/_authed/settings/tax")({
  staticData: {
    section: "preferences",
    parent: { label: "nav.settings", to: "/settings" },
    crumb: "page.tax",
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: TaxPage,
});

type T = AreaTranslate<typeof tax.id>;

const ROUNDINGS = ["nearest", "down", "up"] as const satisfies readonly TaxRounding[];
const KIND_KEYS = {
  beverage: "tax.kind.beverage",
  food: "tax.kind.food",
  retail: "tax.kind.retail",
} as const satisfies Record<ProductKind, string>;
const STATUS_KEYS = {
  current: "tax.status.current",
  scheduled: "tax.status.scheduled",
  replaced: "tax.status.replaced",
  inactive: "tax.status.inactive",
  ended: "tax.status.ended",
} as const satisfies Record<TaxVersionStatus, string>;
const STATUS_VARIANTS = {
  current: "success",
  scheduled: "warning",
  replaced: "outline",
  inactive: "outline",
  ended: "outline",
} as const satisfies Record<TaxVersionStatus, string>;

/** A rule's editable fields: what a new version copies from the latest one. */
const inputOf = (rule: TaxRuleView): TaxRuleInput => ({
  name: rule.name,
  jurisdiction: rule.jurisdiction,
  productKinds: rule.productKinds,
  taxBasisPoints: rule.taxBasisPoints,
  serviceChargeBasisPoints: rule.serviceChargeBasisPoints,
  serviceChargeTaxable: rule.serviceChargeTaxable,
  inclusive: rule.inclusive,
  rounding: rule.rounding,
  active: rule.active,
  startsAt: rule.startsAt,
  endsAt: rule.endsAt,
});

/** POST a first version, or PUT the next one on top of the latest (optimistic `expectedVersion`). */
async function saveVersion(outletId: string, latest: TaxRuleView | null, input: TaxRuleInput) {
  return latest
    ? unwrap(
        await api.v1["tax-rules"]({ taxRuleId: latest.id }).put({
          ...input,
          outletId,
          expectedVersion: latest.version,
        }),
      )
    : unwrap(await api.v1["tax-rules"].post({ ...input, outletId }));
}

function errorMessage(t: T, error: unknown): string {
  if (!(error instanceof ApiRequestError)) return t("common.saveOffline");
  switch (error.code) {
    case "validation_failed":
      return t("tax.invalid");
    case "tax_rule_version_conflict":
      return t("tax.conflict");
    case "forbidden":
      return t("tax.forbidden");
    case "not_found":
      return t("tax.notFound");
    default:
      return t("common.saveFailed");
  }
}

/** A device-local day and time as an ISO instant (a date-time without offset parses as local time). */
const instant = (day: string, time: string) =>
  day ? new Date(`${day}T${time || "00:00"}`).toISOString() : null;

/** The finance view of an outlet's tax (R1-18): the version in force, what's scheduled and the history. */
function TaxPage() {
  const { t } = useMessages(tax);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const managed = outlets.filter((o) => canAt(scopes, "tax.manage", o.id));
  const [outletId, setOutletId] = useState(
    managed.find((o) => o.id === current?.id)?.id ?? managed[0]?.id ?? "",
  );
  const outlet = managed.find((o) => o.id === outletId);
  const versions = useInfiniteQuery(taxRulesQuery(outletId));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const columns = useMemo(() => versionColumns(t), [t]);
  const loaded = versions.data?.pages.flatMap((page) => page.items) ?? [];
  const latest = loaded[0] ?? null;
  const now = Date.now();
  const inForce = ruleInForce(loaded, now);

  const disable = useMutation({
    mutationFn: async () => {
      if (!latest) throw new Error("no rule");
      const off = { ...inputOf(latest), active: false, startsAt: null, endsAt: null };
      return saveVersion(outletId, latest, off);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: [...taxRulesKey, outletId] });
      toast.success(t("tax.disabled", { version: saved.version }));
    },
    onError: (error) => toast.error(errorMessage(t, error)),
  });

  if (!outlet) {
    return (
      <Page>
        <PageHeader title={t("page.tax")} subtitle={t("tax.noOutlet")} />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        title={t("page.tax")}
        subtitle={t("tax.subtitle")}
        actions={
          <>
            {inForce && (
              <ConfirmButton
                variant="outline"
                title={t("tax.disableTitle", { outlet: outlet.name })}
                description={t("tax.disableHint")}
                confirmLabel={t("tax.disable")}
                disabled={disable.isPending || versions.isPending}
                onConfirm={() => disable.mutate()}
              >
                {t("tax.disable")}
              </ConfirmButton>
            )}
            <Button type="button" disabled={versions.isPending} onClick={() => setEditing(true)}>
              {latest ? <PencilIcon /> : <PlusIcon />}
              {t(latest ? "tax.edit" : "tax.create")}
            </Button>
          </>
        }
      />
      {managed.length > 1 && (
        // A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine).
        <OptionSelect
          aria-label={t("outlet.heading")}
          className="md:w-64"
          options={managed.map((o) => ({ value: o.id, label: o.name }))}
          value={outletId}
          onValueChange={setOutletId}
        />
      )}
      <CurrentRule
        loading={versions.isPending}
        rule={inForce}
        latest={latest}
        scheduled={scheduledRules(loaded, now)}
        timezone={outlet.timezone}
      />
      <section className="flex flex-col gap-3" aria-label={t("tax.history")}>
        <h2 className="font-semibold text-lg">{t("tax.history")}</h2>
        <DataTable
          paging="pages"
          label={t("tax.history")}
          columns={columns}
          query={versions}
          getRowId={(rule) => rule.id}
          empty={{ title: t("tax.emptyTitle"), description: t("tax.emptyHint") }}
        />
      </section>
      {editing && (
        <RuleDialog
          outlet={outlet}
          latest={latest}
          onClose={() => setEditing(false)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: [...taxRulesKey, outletId] })}
        />
      )}
    </Page>
  );
}

function RuleSummary(props: { rule: TaxRuleView }) {
  const { t, locale } = useMessages(tax);
  const { rule } = props;
  return (
    <ul className="flex flex-col gap-1 text-sm">
      <li>
        {t("tax.ratesValue", {
          tax: percentText(rule.taxBasisPoints, locale),
          service: percentText(rule.serviceChargeBasisPoints, locale),
        })}
      </li>
      <li className="text-muted-foreground">
        {t(rule.inclusive ? "tax.inclusive" : "tax.exclusive")} ·{" "}
        {rule.productKinds.map((kind) => t(KIND_KEYS[kind])).join(", ")}
      </li>
      {rule.serviceChargeBasisPoints > 0 && rule.serviceChargeTaxable && (
        <li className="text-muted-foreground">{t("tax.serviceTaxable")}</li>
      )}
    </ul>
  );
}

function CurrentRule(props: {
  loading: boolean;
  rule: TaxRuleView | null;
  latest: TaxRuleView | null;
  scheduled: TaxRuleView[];
  timezone: string;
}) {
  const { t, format } = useMessages(tax);
  const { rule, latest } = props;
  const hint = rule
    ? `${rule.jurisdiction} · ${t("tax.versionNumber", { version: rule.version })}`
    : !latest
      ? t("tax.noneHint")
      : latest.endsAt && Date.parse(latest.endsAt) <= Date.now()
        ? t("tax.endedHint", { time: format.dateTime(latest.endsAt, props.timezone) })
        : t("tax.disabledHint");
  if (props.loading) return null;
  return (
    <Card className="gap-4">
      <CardHeader className="grid-cols-[auto_1fr] items-center gap-x-3">
        <span className="row-span-2 inline-flex size-10 items-center justify-center rounded-lg bg-muted text-primary [&_svg]:size-5">
          <ReceiptTextIcon />
        </span>
        <CardTitle>
          {t("tax.current")}: {rule ? rule.name : t("tax.none")}
        </CardTitle>
        <CardDescription>{hint}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {rule && <RuleSummary rule={rule} />}
        {props.scheduled.map((next) => (
          <Badge key={next.id} variant="warning">
            {t("tax.scheduled", {
              version: next.version,
              time: format.dateTime(next.startsAt ?? "", props.timezone),
            })}
          </Badge>
        ))}
      </CardContent>
    </Card>
  );
}

function StatusCell(props: { rule: TaxRuleView; versions: TaxRuleView[] }) {
  const { t } = useMessages(tax);
  const status = versionStatus(props.rule, props.versions, Date.now());
  return <Badge variant={STATUS_VARIANTS[status]}>{t(STATUS_KEYS[status])}</Badge>;
}

function PeriodCell(props: { rule: TaxRuleView }) {
  const { t, format } = useMessages(tax);
  const { startsAt, endsAt } = props.rule;
  const text =
    startsAt && endsAt
      ? t("tax.range", { start: format.dateTime(startsAt), end: format.dateTime(endsAt) })
      : startsAt
        ? t("tax.from", { time: format.dateTime(startsAt) })
        : endsAt
          ? t("tax.until", { time: format.dateTime(endsAt) })
          : t("tax.always");
  return <span className="text-sm">{text}</span>;
}

function versionColumns(t: T): DataTableColumn<TaxRuleView>[] {
  return [
    {
      id: "version",
      header: t("tax.version"),
      meta: { card: "title", className: "md:w-24" },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {t("tax.versionNumber", { version: row.original.version })}
        </span>
      ),
    },
    {
      id: "rule",
      header: t("tax.rule"),
      meta: { card: "subtitle" },
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate">{row.original.name}</span>
          <span className="truncate text-muted-foreground text-sm">{row.original.jurisdiction}</span>
        </div>
      ),
    },
    { id: "rates", header: t("tax.rates"), cell: ({ row }) => <RuleSummary rule={row.original} /> },
    { id: "period", header: t("tax.period"), cell: ({ row }) => <PeriodCell rule={row.original} /> },
    {
      id: "status",
      header: t("tax.status"),
      meta: { className: "md:w-32" },
      cell: ({ row, table }) => (
        <StatusCell rule={row.original} versions={table.getCoreRowModel().rows.map((r) => r.original)} />
      ),
    },
  ];
}

function Field(props: { id: string; label: string; hint?: string; error?: boolean; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      {props.children}
      {props.hint && (
        <p className={props.error ? "text-destructive text-sm" : "text-muted-foreground text-sm"}>
          {props.hint}
        </p>
      )}
    </div>
  );
}

/** The next version of an outlet's rule, prefilled from the latest. Mounted only while open. */
function RuleDialog(props: {
  outlet: OutletSummary;
  latest: TaxRuleView | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { outlet, latest } = props;
  const { t, format, locale } = useMessages(tax);
  const base = latest ? inputOf(latest) : null;
  const [name, setName] = useState(base?.name ?? "");
  const [jurisdiction, setJurisdiction] = useState(base?.jurisdiction ?? "");
  const [rate, setRate] = useState(base ? basisPointsInput(base.taxBasisPoints) : "10");
  const [serviceRate, setServiceRate] = useState(
    base ? basisPointsInput(base.serviceChargeBasisPoints) : "0",
  );
  const [kinds, setKinds] = useState<ProductKind[]>(base?.productKinds ?? ["beverage", "food"]);
  const [inclusive, setInclusive] = useState(base?.inclusive ?? false);
  const [serviceTaxable, setServiceTaxable] = useState(base?.serviceChargeTaxable ?? false);
  const [rounding, setRounding] = useState<TaxRounding>(base?.rounding ?? "nearest");
  const [active, setActive] = useState(true);
  // A new version starts now unless scheduled; the latest's own period isn't copied.
  const [startDay, setStartDay] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDay, setEndDay] = useState("");
  const [endTime, setEndTime] = useState("23:59");
  const ids = {
    name: useId(),
    jurisdiction: useId(),
    rate: useId(),
    service: useId(),
    pricing: useId(),
    rounding: useId(),
    start: useId(),
    startLabel: useId(),
    startTime: useId(),
    end: useId(),
    endLabel: useId(),
    endTime: useId(),
  };

  const taxPoints = parseBasisPoints(rate);
  const servicePoints = parseBasisPoints(serviceRate);
  const startsAt = instant(startDay, startTime);
  const endsAt = instant(endDay, endTime);
  const periodInvalid = startsAt !== null && endsAt !== null && Date.parse(endsAt) <= Date.parse(startsAt);
  const input: TaxRuleInput | null =
    name.trim() && jurisdiction.trim() && taxPoints !== null && servicePoints !== null && kinds.length > 0
      ? {
          name: name.trim(),
          jurisdiction: jurisdiction.trim(),
          productKinds: kinds,
          taxBasisPoints: taxPoints,
          serviceChargeBasisPoints: servicePoints,
          serviceChargeTaxable: serviceTaxable,
          inclusive,
          rounding,
          active,
          startsAt,
          endsAt,
        }
      : null;
  // The same arithmetic the register uses, on a taxable Rp100,000 order.
  const example = input
    ? priceTax([{ position: 0, amount: 100_000n, taxable: true }], {
        productKinds: input.productKinds,
        taxBasisPoints: input.taxBasisPoints,
        serviceChargeBasisPoints: input.serviceChargeBasisPoints,
        serviceChargeTaxable: input.serviceChargeTaxable,
        inclusive: input.inclusive,
        rounding: input.rounding,
      })
    : null;

  const save = useMutation({
    mutationFn: async () => {
      if (!input) throw new Error("incomplete");
      return saveVersion(outlet.id, latest, input);
    },
    onSuccess: (saved) => {
      props.onSaved();
      toast.success(t("tax.saved", { version: saved.version }));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && input && !periodInvalid) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("tax.formTitle", { outlet: outlet.name })}
      description={t("tax.formHint")}
      dismissible={!save.isPending}
      className="md:max-w-xl"
      footer={
        <Button type="submit" form="tax-rule" disabled={save.isPending || !input || periodInvalid}>
          {t(save.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="tax-rule" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field id={ids.name} label={t("tax.name")}>
            <Input
              id={ids.name}
              value={name}
              maxLength={80}
              placeholder={t("tax.namePlaceholder")}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field id={ids.jurisdiction} label={t("tax.jurisdiction")}>
            <Input
              id={ids.jurisdiction}
              value={jurisdiction}
              maxLength={120}
              placeholder={t("tax.jurisdictionPlaceholder")}
              onChange={(e) => setJurisdiction(e.target.value)}
            />
          </Field>
          <Field
            id={ids.rate}
            label={t("tax.rate")}
            {...(taxPoints === null ? { hint: t("tax.rateInvalid"), error: true } : {})}
          >
            <Input
              id={ids.rate}
              inputMode="decimal"
              value={rate}
              aria-invalid={taxPoints === null}
              onChange={(e) => setRate(e.target.value)}
            />
          </Field>
          <Field
            id={ids.service}
            label={t("tax.serviceRate")}
            {...(servicePoints === null ? { hint: t("tax.rateInvalid"), error: true } : {})}
          >
            <Input
              id={ids.service}
              inputMode="decimal"
              value={serviceRate}
              aria-invalid={servicePoints === null}
              onChange={(e) => setServiceRate(e.target.value)}
            />
          </Field>
          <Field id={ids.pricing} label={t("tax.pricing")}>
            <OptionSelect
              id={ids.pricing}
              options={[
                { value: "exclusive" as const, label: t("tax.exclusive") },
                { value: "inclusive" as const, label: t("tax.inclusive") },
              ]}
              value={inclusive ? "inclusive" : "exclusive"}
              onValueChange={(value) => setInclusive(value === "inclusive")}
            />
          </Field>
          <Field id={ids.rounding} label={t("tax.rounding")}>
            <OptionSelect
              id={ids.rounding}
              options={ROUNDINGS.map((value) => ({ value, label: t(`tax.rounding.${value}`) }))}
              value={rounding}
              onValueChange={setRounding}
            />
          </Field>
        </div>
        <fieldset className="flex flex-col gap-1">
          <legend className="font-medium text-sm">{t("tax.kinds")}</legend>
          <p className="text-muted-foreground text-sm">{t("tax.kindsHint")}</p>
          <div className="flex flex-wrap gap-x-4">
            {PRODUCT_KINDS.map((kind) => (
              <Label key={kind} className="min-h-11 font-normal">
                <Checkbox
                  checked={kinds.includes(kind)}
                  onCheckedChange={(checked) =>
                    setKinds((current) =>
                      checked === true
                        ? PRODUCT_KINDS.filter((k) => k === kind || current.includes(k))
                        : current.filter((k) => k !== kind),
                    )
                  }
                />
                {t(KIND_KEYS[kind])}
              </Label>
            ))}
          </div>
        </fieldset>
        <Label className="min-h-11 justify-between font-normal">
          {t("tax.serviceTaxable")}
          <Switch checked={serviceTaxable} onCheckedChange={setServiceTaxable} />
        </Label>
        <Label className="min-h-11 justify-between font-normal">
          {t("tax.active")}
          <Switch checked={active} onCheckedChange={setActive} />
        </Label>
        <fieldset className="flex flex-col gap-2">
          <legend className="font-medium text-sm">{t("tax.period")}</legend>
          <div className="grid gap-3 sm:grid-cols-[1fr_8rem]">
            <div className="flex flex-col gap-1.5">
              <Label id={ids.startLabel} htmlFor={ids.start}>
                {t("tax.startsAt")}
              </Label>
              <DatePicker
                id={ids.start}
                labelId={ids.startLabel}
                value={startDay}
                onChange={setStartDay}
                locale={locale}
              />
            </div>
            <Field id={ids.startTime} label={t("tax.startTime")}>
              <Input
                id={ids.startTime}
                type="time"
                value={startTime}
                disabled={!startDay}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </Field>
            <div className="flex flex-col gap-1.5">
              <Label id={ids.endLabel} htmlFor={ids.end}>
                {t("tax.endsAt")}
              </Label>
              <DatePicker
                id={ids.end}
                labelId={ids.endLabel}
                value={endDay}
                onChange={setEndDay}
                locale={locale}
              />
            </div>
            <Field id={ids.endTime} label={t("tax.endTime")}>
              <Input
                id={ids.endTime}
                type="time"
                value={endTime}
                disabled={!endDay}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </Field>
          </div>
          <p className={periodInvalid ? "text-destructive text-sm" : "text-muted-foreground text-sm"}>
            {t(periodInvalid ? "tax.endBeforeStart" : "tax.periodHint")}
          </p>
        </fieldset>
        {example && (
          <p role="status" className="rounded-lg bg-muted p-3 text-sm">
            {t("tax.example", {
              service: format.money(BigInt(example.serviceCharge)),
              tax: format.money(BigInt(example.tax)),
              total: format.money(BigInt(example.total)),
            })}
          </p>
        )}
        {save.isError && (
          <p role="alert" className="text-destructive text-sm">
            {errorMessage(t, save.error)}
          </p>
        )}
      </form>
    </ResponsiveDialog>
  );
}
